/**
 * Clean public API for Peace EQ application
 * Provides external access without polluting the global window object
 */

import type { ServiceContainer } from './service-container';
import type { AppController } from '../features/equalizer/app-controller';
import type { EQSetting, TestResults, EqualLoudnessContour } from '../types';

export class PeaceEQAPI {
    constructor(private container: ServiceContainer) {}

    // Graph rendering
    drawGraph(): void {
        const appController = this.container.get<AppController>('appController');
        appController.drawGraph();
    }

    // Configuration management
    saveConfiguration(): void {
        const appController = this.container.get<AppController>('appController');
        return appController.saveConfiguration();
    }

    loadConfiguration(event?: Event): void {
        const appController = this.container.get<AppController>('appController');
        return appController.loadConfiguration(event);
    }

    resetResults(): void {
        const appController = this.container.get<AppController>('appController');
        return appController.resetResults();
    }

    calculateEqualization(): void {
        const appController = this.container.get<AppController>('appController');
        return appController.calculateEqualization();
    }

    // EQ settings generation
    generateEqSettings(): EQSetting[] | undefined {
        const appController = this.container.get<AppController>('appController');
        return appController.generateEqSettings();
    }

    // Data access
    getTestResults(): TestResults {
        const appController = this.container.get<AppController>('appController');
        return appController.getTestResults();
    }

    getEqualLoudnessContours(): { [key: string]: EqualLoudnessContour[] } {
        const appController = this.container.get<AppController>('appController');
        return appController.getEqualLoudnessContours();
    }

    getCurrentFrequency(): number {
        const appController = this.container.get<AppController>('appController');
        return appController.getCurrentFrequency();
    }

    // Audio control
    getIsStarted(): boolean {
        const appController = this.container.get<AppController>('appController');
        return appController.getIsStarted();
    }

    startAudio(): void {
        const appController = this.container.get<AppController>('appController');
        return appController.startAudio();
    }

    stopAudio(): void {
        const appController = this.container.get<AppController>('appController');
        return appController.stopAudio();
    }

    setNoiseVolume(volume: number): void {
        const appController = this.container.get<AppController>('appController');
        return appController.setNoiseVolume(volume);
    }

    setSineVolume(volume: number): void {
        const appController = this.container.get<AppController>('appController');
        return appController.setSineVolume(volume);
    }

    setSineFrequency(frequency: number): void {
        const appController = this.container.get<AppController>('appController');
        return appController.setSineFrequency(frequency);
    }

    // State management
    updateResult(frequency: number, db: number, ear: 'left' | 'right' | 'both'): void {
        const appController = this.container.get<AppController>('appController');
        return appController.updateResult(frequency, db, ear);
    }

    // Utility method to add sample data for testing
    addSampleData(): void {
        const samplePoints = [
            { frequency: 125, db: -10 },
            { frequency: 250, db: -15 },
            { frequency: 500, db: -20 },
            { frequency: 1000, db: -25 },
            { frequency: 2000, db: -30 },
            { frequency: 4000, db: -35 },
            { frequency: 8000, db: -40 }
        ];
        
        samplePoints.forEach(point => {
            this.updateResult(point.frequency, point.db, 'both');
        });
        
        this.drawGraph();
    }
}